import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import styles from './UserDocumentsPage.module.css';
import Button from '../../../components/common/Button/Button';
import { REACT_APP_API_BASE_URL } from '../../../apiurl';
import { formatAdminDate, formatAdminDateTime } from '../../../utils/datetime';

const SearchIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const UserDocumentsPage = () => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState(null);

    // Filters & Pagination
    const [userIdFilter, setUserIdFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    // Verification Modal State
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [verificationNote, setVerificationNote] = useState('');
    const [verificationStatus, setVerificationStatus] = useState('Verified'); // Verified | Rejected
    const [showModal, setShowModal] = useState(false);

    const fetchDocuments = useCallback(async () => {
        setLoading(true);
        try {
            let url = `${REACT_APP_API_BASE_URL}api/document?page=${currentPage}&limit=${itemsPerPage}`;
            if (userIdFilter) {
                url += `&userId=${userIdFilter}`;
            }

            const response = await axios.get(url);
            if (response.data && response.data.status) {
                setDocuments(response.data.data);
                if (response.data.pagination) {
                    setTotalPages(response.data.pagination.totalPages);
                    setTotalItems(response.data.pagination.totalItems || response.data.data.length);
                } else {
                    setTotalItems(response.data.data.length);
                }
            } else {
                setDocuments([]);
                setTotalItems(0);
            }
        } catch (err) {
            console.error("Error fetching documents:", err);
            setErrorMsg("Failed to load documents");
        } finally {
            setLoading(false);
        }
    }, [currentPage, userIdFilter, itemsPerPage]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    const handleSearch = () => {
        setCurrentPage(1);
        fetchDocuments();
    };

    const handleSearchKey = (e) => {
        if (e.key === "Enter") {
            handleSearch();
        }
    };

    const handleVerifyClick = (doc) => {
        setSelectedDoc(doc);
        setVerificationNote(doc.verifiednote || '');
        setVerificationStatus(doc.verifiedtype || 'Verified');
        setShowModal(true);
    };

    const submitVerification = async () => {
        if (!selectedDoc) return;

        try {
            const response = await axios.put(`${REACT_APP_API_BASE_URL}api/document/${selectedDoc.documentid}/verify`, {
                status: verificationStatus,
                note: verificationNote,
                adminName: "Admin" // You might want to get this from session/auth
            });

            if (response.data && response.data.status) {
                // Update local state without full reload
                setDocuments(prev => prev.map(d =>
                    d.documentid === selectedDoc.documentid
                        ? { ...d, ...response.data.data }
                        : d
                ));
                setShowModal(false);
            } else {
                alert("Failed to update verification status");
            }
        } catch (err) {
            console.error("Verification error:", err);
            alert("Error updating verification");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this document?")) return;

        try {
            await axios.delete(`${REACT_APP_API_BASE_URL}api/document/${id}`);
            fetchDocuments(); // Reload list
        } catch (err) {
            console.error("Delete error:", err);
            alert("Failed to delete document");
        }
    };

    const handleViewFile = (id) => {
        // Open file in new tab
        window.open(`${REACT_APP_API_BASE_URL}api/document/${id}/file`, '_blank');
    };

    const SkeletonRows = () => (
        <>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <tr key={i} className={styles.skeletonRow}>
                    <td><span className={`${styles.skel} ${styles.skel40}`} /></td>
                    <td><span className={`${styles.skel} ${styles.skel60}`} /></td>
                    <td><span className={`${styles.skel} ${styles.skel60}`} /></td>
                    <td><span className={`${styles.skel} ${styles.skel140}`} /></td>
                    <td><span className={`${styles.skel} ${styles.skel100}`} /></td>
                    <td><span className={`${styles.skel} ${styles.skel80}`} /></td>
                    <td><span className={`${styles.skel} ${styles.skel140}`} /></td>
                </tr>
            ))}
        </>
    );

    return (
        <div className={styles.pageContainer}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h1>User Documents</h1>
                    <p>{totalItems} document{totalItems !== 1 ? "s" : ""} found</p>
                </div>
            </div>

            <div className={`${styles.filtersSection} ${styles.filtersRightAlign}`}>
                <div className={styles.filtersRight}>
                    <div className={styles.searchWrap}>
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder="Search by User ID..."
                            value={userIdFilter}
                            onChange={(e) => setUserIdFilter(e.target.value)}
                            onKeyDown={handleSearchKey}
                        />
                    </div>
                    <button type="button" className={styles.btnSearch} onClick={handleSearch}>
                        Search
                    </button>
                </div>
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.docTable}>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>User ID</th>
                            <th>Type ID</th>
                            <th>Filename</th>
                            <th>Upload Date</th>
                            <th>Verified Status</th>
                            <th className={styles.center}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <SkeletonRows />
                        ) : documents.length === 0 ? (
                            <tr className={styles.emptyRow}>
                                <td colSpan={7}>
                                    <div className={styles.emptyIcon}>No Data</div>
                                    <h3>No documents found</h3>
                                    <p>Try adjusting your search filters.</p>
                                </td>
                            </tr>
                        ) : (
                            documents.map(doc => (
                                <tr key={doc.documentid}>
                                    <td><span className={styles.cellId}>#{doc.documentid}</span></td>
                                    <td><div className={styles.cellName}>{doc.userid}</div></td>
                                    <td>{doc.documenttypeid}</td>
                                    <td title={doc.filename} className={styles.filenameCell}>{doc.filename || '-'}</td>
                                    <td>
                                        <span className={styles.docMetaText}>
                                            {formatAdminDateTime(doc.uploaddate, '-')}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`${styles.statusBadge} ${doc.verifiedtype === 'Verified' ? styles.active : doc.verifiedtype === 'Rejected' ? styles.inactive : styles.pending}`}>
                                            {doc.verifiedtype || 'Pending'}
                                        </span>
                                    </td>
                                    <td className={styles.center}>
                                        <div className={styles.actionsCell}>
                                            <Button size="small" variant="secondary" onClick={() => handleViewFile(doc.documentid)} disabled={!doc.has_doc}>
                                                View
                                            </Button>
                                            <Button size="small" variant="primary" onClick={() => handleVerifyClick(doc)}>
                                                Verify
                                            </Button>
                                            <Button size="small" variant="danger" onClick={() => handleDelete(doc.documentid)}>
                                                Delete
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                {!loading && documents.length > 0 && (
                    <div className={styles.tableFooter}>
                        <span>
                            Showing {documents.length} of {totalItems} documents
                        </span>

                        {totalPages > 1 && (
                            <div className={styles.pagination}>
                                <button
                                    type="button"
                                    className={styles.pageBtn}
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage((prev) => prev - 1)}
                                >
                                    {"<"}
                                </button>

                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                                    if (
                                        page === 1 ||
                                        page === totalPages ||
                                        (page >= currentPage - 1 && page <= currentPage + 1)
                                    ) {
                                        return (
                                            <button
                                                type="button"
                                                key={page}
                                                className={`${styles.pageBtn} ${currentPage === page ? styles.activePage : ""}`}
                                                onClick={() => setCurrentPage(page)}
                                            >
                                                {page}
                                            </button>
                                        );
                                    }

                                    if (page === currentPage - 2 || page === currentPage + 2) {
                                        return page > 3 && (
                                            <span key={page} className={styles.pagerSeparator}>
                                                ...
                                            </span>
                                        );
                                    }

                                    return null;
                                })}

                                <button
                                    type="button"
                                    className={styles.pageBtn}
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage((prev) => prev + 1)}
                                >
                                    {">"}
                                </button>
                            </div>
                        )}

                        <span className={styles.refreshTime}>
                            Last refreshed: {formatAdminDate(new Date())}
                        </span>
                    </div>
                )}
            </div>

            {/* Verification Modal */}
            {showModal && (
                <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
                    <div className={styles.modal}>
                        <div className={styles.modalHeader}>
                            <h2>Verify Document #{selectedDoc?.documentid}</h2>
                            <button
                                type="button"
                                className={styles.modalClose}
                                onClick={() => setShowModal(false)}
                            >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <div className={styles.formGroup}>
                                <label>Status <span className={styles.required}>*</span></label>
                                <select value={verificationStatus} onChange={(e) => setVerificationStatus(e.target.value)} className={styles.select}>
                                    <option value="Verified">Verified</option>
                                    <option value="Rejected">Rejected</option>
                                    <option value="Pending">Pending</option>
                                </select>
                            </div>

                            <div className={styles.formGroup}>
                                <label>Note</label>
                                <textarea
                                    value={verificationNote}
                                    onChange={(e) => setVerificationNote(e.target.value)}
                                    className={styles.textarea}
                                    placeholder="Enter verification notes..."
                                />
                            </div>
                        </div>

                        <div className={styles.modalFooter}>
                            <button type="button" className={styles.btnCancel} onClick={() => setShowModal(false)}>
                                Cancel
                            </button>
                            <button type="button" className={styles.btnSave} onClick={submitVerification}>
                                Save Status
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserDocumentsPage;
